import {
  Box,
  CssBaseline,
  Typography,
  Grid2,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import { useEffect, useState } from "react";
import { getCreds } from "../contexts/PageContext";
import { Org } from "../models/Org";
import { loadOrgRecords } from "../api/connection";
import { createRobot, Robot, RobotManifest } from "../models/Robot";
import { createRemoteControl, RemoteControl } from "../models/RemoteControl";
import { BlobOrgImage } from "../components/BlogOrgImage";
import { createDeployment, Deployment } from "../models/Deployment";
import * as yaml from 'js-yaml';

interface RemoteAssignment {
  robot: Robot;
  stagedRobotVersion: string;
  remote: RemoteControl;
  deployment: Deployment;
}

export default function PortalHome() {
  const [organization, setOrganization] = useState<Org | null>(null);
  const [robots, setRobots] = useState<Robot[]>([]);
  const [remotes, setRemotes] = useState<RemoteControl[]>([]);
  const [assignments, setAssignments] = useState<RemoteAssignment[]>([]);
  useEffect(() => {
    const loader = async () => {
      const { org } = getCreds();
      const rs = await loadOrgRecords('robots', createRobot);
      const remotes = await loadOrgRecords('remotecontrols', createRemoteControl);
      const ds = await loadOrgRecords('deployments', createDeployment);
      const as: RemoteAssignment[] = rs.map((r) => {
        const remote = remotes.find((rem) => rem.robotId === r._id);
        const manifest = yaml.load(r.yamlContent) as RobotManifest;
        return {
          robot: r,
          stagedRobotVersion: manifest?.softwareVersion || '',
          remote: remote || createRemoteControl(),
          deployment: ds.find((d) => d._id === remote?.deploymentId) || createDeployment(),
        };
      });
      for (const remote of remotes) {
        if (!as.find((a) => a.remote._id === remote._id)) {
          as.push({
            robot: createRobot(),
            remote: remote,
            stagedRobotVersion: '',
            deployment: ds.find((d) => d._id === remote.deploymentId) || createDeployment(),
          });
        }
      }
      setAssignments(as);
      setOrganization(org);
    };
    loader();
  }, []);
  if (!organization) {
    return (
      <Backdrop
        open={true}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>);
  }
  // console.log("Organization loaded:", organization);
  return (
    <Box sx={{ display: "flex" }}>
      {/* page header */}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <CssBaseline />
        <PageHeader
          title={<div style={{ display: "flex", alignItems: "center" }}>
            {`Dashboard - ${organization.name}`}
          </div>}
          rightComponent={[
            <BlobOrgImage blobId={organization.imageBlobId} style={{ display: "inline-block", maxWidth: 116, marginRight: 10 }} />
            // <Minibar
            //   minibarItems={[
            //     {
            //       text: "Upload HMI version",
            //       icon: <Style />,
            //       onClick: () => void 0,
            //       // onClick: () => setIsSkyboxMenuOpen((prev) => !prev),
            //     },
            //   ]}
            //   barLength={1}
            // />,
          ]}
        />
        <Container sx={{
          backgroundColor: "#252525",
          minHeight: "calc(100vh - 80px)",
          pt: "1rem",
          pl: "2rem",
          pr: "2rem",
          pb: "2rem"
        }} maxWidth={false}>
          <Grid2 container spacing={1}>
            {/* Robot Status */}
            <Grid2 size={{ xs: 12, md: 12 }}>
              <Typography variant="h4" sx={{ fontWeight: "bold", ml: 1, mb: 2 }}>
                Robots and Remotes
              </Typography>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>ROBOT</TableCell>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>ROBOT VERSION</TableCell>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>REMOTE</TableCell>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>REMOTE VERSION</TableCell>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>DEPLOYMENT</TableCell>
                      <TableCell style={{ fontSize: 20, color: '#AAAAAA' }}>INFO</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((row) => (
                      <TableRow
                        key={`${row.robot._id}-${row.remote._id}`}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, verticalAlign: 'top' }}
                      >
                        <TableCell component="th" scope="row">
                          {row.robot._id === '' ? <i>Unassigned</i> :
                            <>
                              <Link style={{ color: 'white', fontWeight: 'bold' }} color="secondary" href={`/edit-robot/${row.robot._id}`} >{row.robot.name}</Link>
                              <div style={{ fontSize: '0.8em', color: '#AAAAAA' }}>SN: {row.robot.serialNumber}</div>
                            </>
                          }
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {`${row.robot.version}${(row.stagedRobotVersion !== row.robot.version) ? ` (${row.stagedRobotVersion})` : ''}`}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {row.remote._id === '' ? <i>Unassigned</i> :
                            <>
                              <Link style={{ color: 'white', fontWeight: 'bold' }} color="secondary" href={`/edit-remote/${row.remote._id}`} >{row.remote.name}</Link>
                              <div style={{ fontSize: '0.8em', color: '#AAAAAA' }}>SN: {row.remote.serialNumber}</div>
                            </>
                          }
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {row.remote.version}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}  >
                          {row.deployment._id === '' ? <i>Unassigned</i> :
                            <Link style={{ color: 'white', fontWeight: 'bold' }} color="secondary" href={`/edit-deployment/${row.deployment._id}`} >{row.deployment.name}</Link>
                          }
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {row.remote.description}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>
          </Grid2>
        </Container>
      </Box>
    </Box>
  );
}
